"use client";
import { Button, Heading, Stack, Text } from "@softmax/design-system";
import { spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import type { RefObject } from "react";
import type { CredentialListState } from "./credential-records";
import { CredentialListContent } from "./credential-list-content";

export function CredentialList({
  title,
  description,
  state,
  onCreate,
  onRevoke,
  onRefresh,
  createControlRef,
  headingLevel,
  summary = false,
}: {
  readonly title: string;
  readonly description: string;
  readonly state: CredentialListState;
  readonly onCreate: () => void;
  readonly onRevoke: (credentialId: string) => void;
  readonly onRefresh: () => void;
  readonly createControlRef?: RefObject<HTMLButtonElement | null>;
  readonly headingLevel: 2 | 4;
  /** Show a count and "Mint new token", with the full list behind an expander. */
  readonly summary?: boolean;
}) {
  return (
    <Stack width="auto">
      <div {...stylex.props(styles.heading)}>
        <div>
          <Heading level={headingLevel}>{title}</Heading>
          <Text as="p" size="small" tone="muted">
            {description}
          </Text>
        </div>
        <Button
          ref={createControlRef}
          size="sm"
          aria-disabled={
            state.status === "loading" ||
            (state.status === "ready" && state.refreshing)
          }
          onClick={onCreate}
        >
          {summary ? "Mint new token" : "Create credential"}
        </Button>
      </div>
      <CredentialListContent
        state={state}
        summary={summary}
        onRevoke={onRevoke}
        onRefresh={onRefresh}
      />
    </Stack>
  );
}

const styles = stylex.create({
  heading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: spacing.space12,
  },
});
